import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Crown, Check, Zap, Eye, Bell, Sparkles, MessageCirclePlus } from 'lucide-react';

interface PremiumModalProps {
  onClose: () => void;
  onUpgrade: () => void;
  isPremium: boolean;
  processing: boolean;
  currencySymbol: string;
}

const ORIGINAL_PRICE = 50;
const PRICE = 20;
const PERCENT_OFF = Math.round(((ORIGINAL_PRICE - PRICE) / ORIGINAL_PRICE) * 100); // 60% off

const PERKS = [
  { icon: Bell, text: 'Unlimited reminders — nudge them until they answer' },
  { icon: Eye, text: 'See if they opened your session' },
  { icon: Sparkles, text: 'See the guesses your recipients made' },
  { icon: MessageCirclePlus, text: 'Send one follow-up question per session' },
];

// Opened from the small gem icon in the nav — replaces the old always-on
// banner that made the free product feel gated. Same upgrade flow
// (Paystack via onUpgrade), just tucked behind a tap instead of shouting
// "pay me" on every visit.
export default function PremiumModal({ onClose, onUpgrade, isPremium, processing, currencySymbol }: PremiumModalProps) {
  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        <motion.div
          className="absolute inset-0"
          style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        />

        <motion.div
          className="relative w-full max-w-sm rounded-2xl border border-brand-border overflow-hidden"
          style={{ background: 'var(--color-brand-card)' }}
          initial={{ opacity: 0, y: 30, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 30, scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 340, damping: 30 }}
        >
          <div
            className="flex items-center justify-between px-5 py-4 border-b border-brand-border"
            style={{ background: 'var(--color-brand-bg)' }}
          >
            <span className="text-xs font-bold uppercase tracking-[0.18em] flex items-center gap-2" style={{ color: '#f59e0b' }}>
              <Crown className="w-3.5 h-3.5" />
              Yhuu Premium
            </span>
            <button
              onClick={onClose}
              className="w-7 h-7 rounded-full flex items-center justify-center border border-brand-border transition-colors hover:border-brand-accent text-zinc-500 hover:text-brand-accent"
              style={{ background: 'var(--color-brand-card)' }}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="px-6 pt-6 pb-6">
            {isPremium ? (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="w-5 h-5" style={{ color: '#f59e0b' }} />
                  <p className="text-lg font-bold" style={{ color: 'var(--color-brand-text)' }}>You're on Premium</p>
                </div>
                <p className="text-sm text-zinc-500 mb-2">
                  All perks below are active on your account.
                </p>
              </>
            ) : (
              <p className="text-sm text-zinc-500 mb-2 leading-relaxed">
                Everything you need to actually find out who's paying attention.
              </p>
            )}

            <div className="flex flex-col gap-3 my-5">
              {PERKS.map((perk, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border border-brand-border"
                    style={{ background: 'var(--color-brand-bg)' }}
                  >
                    <perk.icon className="w-4 h-4" style={{ color: '#f59e0b' }} />
                  </div>
                  <p className="text-sm pt-1.5 leading-snug" style={{ color: 'var(--color-brand-text)' }}>{perk.text}</p>
                </div>
              ))}
            </div>

            {!isPremium && (
              <>
                <div className="flex items-end justify-center gap-2 mb-1">
                  <span className="text-lg text-zinc-500 line-through">{currencySymbol}{ORIGINAL_PRICE}</span>
                  <span className="text-3xl font-black" style={{ color: 'var(--color-brand-text)' }}>{currencySymbol}{PRICE}</span>
                  <span className="text-sm text-zinc-500 pb-1">/mo</span>
                </div>
                <div className="flex justify-center mb-5">
                  <span
                    className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full"
                    style={{ background: 'rgba(16,185,129,0.12)', color: '#10b981' }}
                  >
                    {PERCENT_OFF}% off
                  </span>
                </div>

                <button
                  onClick={onUpgrade}
                  disabled={processing}
                  className="w-full py-3.5 rounded-xl font-bold flex items-center justify-center gap-2 transition-all disabled:opacity-50 cursor-pointer"
                  style={{ background: '#f59e0b', color: '#1a1200' }}
                >
                  <Crown className="w-4 h-4" />
                  {processing ? 'Processing...' : 'Upgrade to Premium'}
                </button>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
